import React, { Fragment, useState } from 'react';
import {
  Datagrid,
  Filter,
  TextField,
  TopToolbar,
  usePermissions,
  Button,
  ListController,
  ListContextProvider,
} from 'react-admin';
import { NoPermissions, ReferenceInput } from '~/components/ra';
import { SvgIcon } from '@material-ui/core';
import { withStyles, createStyles } from '@material-ui/core/styles';
import { SettingsIcon } from '~/img';
import { useHistory } from 'react-router-dom';
import MappingsCreate from './MappingsCreate';
import MappingsShow from './MappingsShow';
import { useTranslate } from 'react-admin';
import { ReferenceField } from 'react-admin';
import { FunctionField } from 'react-admin';
import { Mapping } from '~/types/Menu';
import { hasTranslation, Longdash } from '~/utils';
import { SelectInput } from 'react-admin';
import { ListView } from '~/layout';
import { useDefaultListFilterStyles } from '~/hooks/useListFilterStyles';

const l = (key: string): string => `resources.menu.list.${key}`;

const listBodyStyles = (theme: any) => {
  return createStyles({
    content: {
      fontSize: 14,
      paddingLeft: 16,
      paddingRight: 16,
      [theme.breakpoints.up('sm')]: {
        paddingLeft: 24,
        paddingRight: 24,
      },
      paddingTop: 16,
      paddingBottom: 16,
    },
  });
};

const MappingsListActions = () => {
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const history = useHistory();
  if (!permissions) return null;
  return (
    <TopToolbar>
      <MappingsCreate />
      <Button label={t('back')} onClick={() => history.push('/menu')}>
        <SvgIcon>
          <SettingsIcon />
        </SvgIcon>
      </Button>
    </TopToolbar>
  );
};

const MappingsList = (props: any) => {
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const td = (key: string): string => translate(`utils.${key}`);
  const classes = useDefaultListFilterStyles();
  if (!permissions) return null;
  if (!permissions?.includes('dynamicMenu.list')) return <NoPermissions />;

  // const { total, loading, classes } = props;
  // if (loading) {
  //   return <div className={classes.content}>{t('searchInProgress')}</div>;
  // } else if (total === 0) {
  //   return <div className={classes.content}>{t('noResultsFound')}</div>;
  // }
  return (
    <Fragment>
      <ListController {...props}>
        {(controllerProps: any) => {
          return (
            <ListContextProvider value={controllerProps}>
              <ListView
                filters={
                  <Filter className={classes.form}>
                    <ReferenceInput
                      label={t('dynamicMenu')}
                      source='menuId'
                      reference='menu'
                      allowEmpty
                      emptyText={td('any')}
                      alwaysOn
                      className={classes.formControl}
                    >
                      <SelectInput optionText='name' />
                    </ReferenceInput>
                    <SelectInput
                      source='channel'
                      choices={[
                        { id: 'MOBILE', name: translate('resources.menu.channels.MOBILE') },
                        { id: 'WEB', name: translate('resources.menu.channels.WEB') },
                        { id: 'WHATSAPP', name: translate('resources.menu.channels.WHATSAPP') },
                        { id: 'USSD', name: translate('resources.menu.channels.USSD') },
                        { id: 'MIDDLEWARE', name: translate('resources.menu.channels.MIDDLEWARE') },
                        { id: 'OTHER', name: translate('resources.menu.channels.OTHER') },
                      ]}
                      alwaysOn
                      emptyText={translate('utils.any')}
                      className={classes.formControl}
                    />
                    <ReferenceInput
                      source='solutionId'
                      reference='solutions'
                      allowEmpty
                      emptyText={td('any')}
                      alwaysOn
                      className={classes.formControl}
                    >
                      <SelectInput optionText='name' />
                    </ReferenceInput>
                  </Filter>
                }
                {...controllerProps}
                pagination={
                  controllerProps.loading || controllerProps.total === 0 ? null : undefined
                }
                bulkActionButtons={false}
                actions={<MappingsListActions {...props} />}
              >
                <ListBody />
              </ListView>
            </ListContextProvider>
          );
        }}
      </ListController>
    </Fragment>
  );
};

const ListBody = withStyles(listBodyStyles)((props: any) => {
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const [showData, setShowData] = useState(null);
  const handleOpen = (data: any) => {
    setShowData(data);
  };
  const handleClose = () => setShowData(null);
  const { total, loading, classes } = props;
  if (loading) {
    return <div className={classes.content}>{t('searchInProgress')}</div>;
  } else if (total === 0) {
    return <div className={classes.content}>{t('noResultsFound')}</div>;
  }
  return (
    <>
      <MappingsShow record={showData} onClose={handleClose} />
      <Datagrid
        rowClick={(_id: string, _basePath: string, record: any) => handleOpen(record)}
        size='medium'
        selectedIds={null}
      >
        <ReferenceField
          label={t('dynamicMenu')}
          source='menuId'
          reference='menu'
          link={false}
          addLabel={false}
          emptyText={Longdash}
        >
          <TextField source='name' />
        </ReferenceField>
        <FunctionField
          label={t('channel')}
          render={(record: Mapping) =>
            hasTranslation(`resources.menu.channels.${record?.channel}`)
              ? translate(`resources.menu.channels.${record.channel}`)
              : record.channel ?? Longdash
          }
          emptyText={Longdash}
        />
        <ReferenceField
          source='solutionId'
          reference='solutions'
          link={false}
          addLabel={false}
          emptyText={Longdash}
        >
          <TextField source='name' />
        </ReferenceField>
      </Datagrid>
    </>
  );
});

export default MappingsList;
